import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./CygnusGrid.module.css";
import { AgGridReact } from "ag-grid-react";
// You can keep this side-effect import; with modules it isn't strictly required, but harmless.
import {
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  GetContextMenuItemsParams,
  MenuItemDef,
  DefaultMenuItem
} from "ag-grid-community";
import { ThreeCircles } from "react-loader-spinner";
import {
  CygnusGridProps,
  CygnusGridRef,
  ExportFormat,
  GridExportColumn,
  GridExportPayload,
} from "./CygnusGrid.utils";
import {
  exportGridData,
  downloadBlob,
  generateExportFilename,
} from "./CygnusGrid.service";
import { themeQuartz } from "@ag-grid-community/theming";

export const cygnusTheme = themeQuartz.withParams({
  // Typography (affects the whole grid)
  fontFamily: ['"Poppins"', "system-ui"],
  fontSize: "var(--default)", // can be a CSS var or number (px)
  // Headers
  headerFontFamily: ['"Poppins"', "system-ui"],
  headerFontWeight: 400,
  // Colors (UI chrome, headers, tool panels inherit these)
  textColor: "rgba(0,0,0,0.54)",
  headerTextColor: "rgba(0,0,0,0.54)",
});

export const CustomNoRowsOverlay: React.FC = () => {
  return (
    <div className="ag-custom-no-rows">
      🚫 No records found. Please try again.
    </div>
  );
};

export const CustomLoadingOverlay: React.FC = () => {
  return (
    <div className={`ag-custom-loading ${styles.customGridLoader}`}>
      <div>
        <ThreeCircles
          height="50"
          width="50"
          color="#4fa94d"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
          ariaLabel="three-circles-rotating"
          outerCircleColor="var(--theme1_tertiary)"
          innerCircleColor="var(--theme1_secondary)"
          middleCircleColor="var(--theme1_primary)"
        />
      </div>
      <div className={styles.customGridLoaderText}>Loading your records</div>
    </div>
  );
};

// Pull a usable value from an AG filter model entry
const getFilterValue = (def: any) => {
  if (!def) return undefined;
  switch (def.filterType) {
    case "set":
      return Array.isArray(def.values) ? def.values : [];
    case "text":
      return def.filter ?? "";
    case "number":
      return def.type === "equals" ? def.filter : undefined;
    case "date":
      return def;
    default:
      return undefined;
  }
};

// Build your legacy { Group, Status, ... } filter using filterKeyMap and AG's filterModel
const buildLegacyFilterFromModel = (
  filterModel: any,
  filterKeyMap?: Record<string, string>,
) => {
  if (!filterModel || !filterKeyMap) return {};
  const out: Record<string, any> = {};

  for (const [colKey, def] of Object.entries<any>(filterModel)) {
    const backendKey = filterKeyMap[colKey]; // colKey should be colId (or field) used in your colDefs
    if (!backendKey) continue;

    const v = getFilterValue(def);

    // If the AG filter produced an array (set filter), forward the entire array
    // so callers can serialize it as CSV or handle it as multi-value.
    if (Array.isArray(v)) {
      if (v.length > 0) {
        out[backendKey] = v; // <-- array, e.g. ['Pending','Approved']
      }
      // else empty array => don't include the filter at all
      continue;
    }

    // Non-array: preserve the scalar value if present
    if (v !== undefined && v !== null && String(v) !== "") {
      out[backendKey] = v;
    }
  }

  return out;
};

// treat set filters with [] as "no filter"
const stripEmptySetFilters = (model: Record<string, any> | undefined) => {
  const out: Record<string, any> = {};
  if (!model) return out;
  for (const [k, v] of Object.entries(model)) {
    if (v && v.filterType === "set") {
      if (Array.isArray(v.values) && v.values.length > 0) out[k] = v;
    } else if (v) {
      out[k] = v; // keep non-set filters untouched
    }
  }
  return out;
};

const isEmptyObject = (o: Record<string, any>) => Object.keys(o).length === 0;

const CygnusGrid = forwardRef<CygnusGridRef, CygnusGridProps>(
  (
    {
      columnDefs,
      fetchData,
      rowSelection = "multiple",
      searchText = "",
      sourceCode = "",
      filterKeyMap = {},
      dataIdentifier = "id",
      preSelectedRows = [],
      pageSize = 100,
      floatingFilters = false,
      // maxBlocksInCache is not used with SSRM; kept in props for compatibility but not passed to grid
      defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 60,
        suppressFloatingFilterButton: true,
        flex: 1,
        filterParams: {
          defaultToNothingSelected: true,
          refreshValuesOnOpen: false,
          suppressSorting: true,
        },
      },
      cachedIsLoading,
      onLoadingChange,
      onSelectionChange,
      cachedData,
      setCachedData,
      onCellValueChanged,
      singleClickEdit = false,
      suppressRowHoverHighlight = false,
      rowModelType = "serverSide",
      enableRowReorder = false,
      onRowReorder,
      onFirstDataRendered,
      onRowSelected,
      onGridReady: onParentGridReady,
      gridContext,
      // Export props
      gridKey,
      gridParams,
      onExportStart,
      onExportComplete,
      onExportError,
      enableFullExport = true,
    },
    ref,
  ) => {
    const [pageSizeState, setPageSizeState] = useState(pageSize || 30);
    const gridRef = useRef<AgGridReact>(null);
    const initialCacheRef = useRef<{
      records: any[];
      totalCount: number;
    } | null>(null);

    const hasLoadedOnce = useRef(false);
    const [internalLoading, setInternalLoading] = useState(false);
    const effectiveDefaultColDef = React.useMemo(
      () => ({
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 60,
        ...(defaultColDef || {}),
        floatingFilter: floatingFilters, // enforce
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [floatingFilters],
    );
    // helper
    const setGridLoading = (loading: boolean) => {
      const api = gridRef.current?.api as any;
      setInternalLoading(loading);
      if (!api) return;
      // v32+: this is the supported way
      api.setGridOption?.("loading", loading);
    };

    const getDataSource = useCallback((): IServerSideDatasource => {
      return {
        getRows: async (params: IServerSideGetRowsParams) => {
          const { startRow, endRow, sortModel, filterModel } =
            (params as any).request || {};
          try {
            // if (!hasLoadedOnce.current && startRow === 0) {
            setGridLoading(true);
            // }
            onLoadingChange?.(true);

            // If all set-filters are empty → treat as NO FILTER
            const normalizedFilterModel = stripEmptySetFilters(filterModel);
            const isNoFilter = isEmptyObject(normalizedFilterModel);

            // If NO FILTER + we have an initial cache for the first block, serve from cache
            const requestingFirstBlock =
              startRow === 0 && endRow - startRow > 0;
            if (
              isNoFilter &&
              initialCacheRef.current &&
              requestingFirstBlock &&
              (searchText ?? "") === "" && // only short-circuit when not searching
              (!sortModel || sortModel.length === 0) // and not sorting
            ) {
              const { records, totalCount } = initialCacheRef.current;
              setGridLoading(false);
              gridRef.current?.api.hideOverlay(); // make sure "no rows" overlay is hidden
              params.success({
                rowData: records,
                rowCount: Number(totalCount ?? 0),
              });
              return; // no API call
            }

            // Build backend filter from the *normalized* model
            const legacyFilter = buildLegacyFilterFromModel(
              normalizedFilterModel,
              filterKeyMap,
            );

            // Your normal fetch
            const response = await fetchData({
              offset: startRow,
              limit: endRow - startRow,
              sortModel,
              filterModel: normalizedFilterModel,
              searchText,
              sourceCode,
              filter: legacyFilter,
            });

            const { records, totalCount } = response;
            const total_count = Number(totalCount ?? 0); // <-- strict number

            // Keep last block (existing behavior)
            setCachedData?.(records);

            // Capture the initial unfiltered first page for future short-circuits
            if (!hasLoadedOnce.current && isNoFilter && startRow === 0) {
              initialCacheRef.current = { records, totalCount: total_count };
            }

            setGridLoading(false);

            if (records.length === 0 && startRow === 0) {
              gridRef.current?.api.showNoRowsOverlay();
            } else {
              gridRef.current?.api.hideOverlay();
              if (!hasLoadedOnce.current) hasLoadedOnce.current = true;
            }

            params.success({ rowData: records, rowCount: total_count });
          } catch (e) {
            console.error("AG Grid SSRM load error:", e);
            setGridLoading(false);
            gridRef.current?.api.showNoRowsOverlay();
            params.fail();
          } finally {
            onLoadingChange?.(false);
          }
        },
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      fetchData,
      onLoadingChange,
      searchText,
      sourceCode,
      setCachedData,
      filterKeyMap,
    ]);

    // helper
    // const autoSizeAll = (api: GridApi) => {
    //   // defer a tick so headers are fully laid out
    //   setTimeout(() => {
    //     // includeHeader: true ensures menu/sort/filter space is accounted for
    //     // Works in v34 (merged API)
    //     (api as any).autoSizeAllColumns?.(undefined, /* skipHeader */ false) ??
    //       api.autoSizeAllColumns(); // fallback for typings variance
    //   }, 0);
    // };

    /** Grid Ready */
    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        // Version-proof: set via grid option (works when SSRM is registered)
        if (rowModelType === "serverSide") {
          params.api.setGridOption("serverSideDatasource", getDataSource());
        }
        params.api.closeToolPanel(); // ensures it starts closed
        onParentGridReady?.(params);
        if (cachedIsLoading) setGridLoading(true); // seed loader
      },
      [getDataSource, cachedIsLoading, rowModelType],
    );

    useEffect(() => {
      if (cachedIsLoading === undefined) return;
      setGridLoading(!!cachedIsLoading);
    }, [cachedIsLoading]);

    /** Pre-select rows after data is available */
    useEffect(() => {
      if (!gridRef.current?.api || !preSelectedRows?.length) return;

      const idSet = new Set(preSelectedRows.map((row) => row[dataIdentifier]));
      setTimeout(() => {
        gridRef.current?.api?.forEachNode((node) => {
          if (idSet.has(node.data?.[dataIdentifier])) {
            node.setSelected(true);
          }
        });
      }, 0);
    }, [preSelectedRows, dataIdentifier]);

    /** Re-fetch on search text change */
    const previousSearchTextRef = useRef<string | null>(null);
    useEffect(() => {
      if (!gridRef.current?.api) return;

      if (previousSearchTextRef.current === null && searchText === "") {
        previousSearchTextRef.current = searchText;
        return;
      }

      if (previousSearchTextRef.current !== searchText) {
        previousSearchTextRef.current = searchText;
        hasLoadedOnce.current = false;
        gridRef.current.api.setGridOption(
          "serverSideDatasource",
          getDataSource(),
        );
      }
    }, [getDataSource, searchText]);

    // /** Re-fetch on external filters change */
    // const previousFilterRef = useRef<any>(null);
    // useEffect(() => {
    //   if (!gridRef.current?.api) return;

    //   const prev = previousFilterRef.current;
    //   const curr = filter;
    //   const changed =
    //     !prev || prev.Group !== curr.Group || prev.Status !== curr.Status;

    //   if (changed) {
    //     previousFilterRef.current = curr;
    //     hasLoadedOnce.current = false;
    //     gridRef.current.api.setGridOption(
    //       "serverSideDatasource",
    //       getDataSource()
    //     );
    //   }
    // }, [getDataSource, filter]);

    const [isExporting, setIsExporting] = useState(false);

    // ============================================
    // EXPORT FUNCTIONALITY
    // ============================================

    /**
     * Get current column state (order, visibility, headers)
     */
    const getCurrentColumnState = useCallback((): GridExportColumn[] => {
      const api = gridRef.current?.api;
      if (!api) return [];

      const columnState = api.getColumnState();
      const columns: GridExportColumn[] = [];

      columnState.forEach((colState, index) => {
        if (colState.hide) return; // Skip hidden columns

        const colDef = columnDefs.find(
          (cd) => cd.field === colState.colId || cd.colId === colState.colId,
        );

        if (colDef?.field) {
          columns.push({
            field: colDef.field,
            headerName: (colDef.headerName as string) || colDef.field,
            order: index,
          });
        }
      });

      return columns.sort((a, b) => a.order - b.order);
    }, [columnDefs]);

    /**
     * Get current sort state
     */
    const getCurrentSortModel = useCallback(() => {
      const api = gridRef.current?.api;
      if (!api) return [];

      return api
        .getColumnState()
        .filter((col) => col.sort)
        .map((col) => ({
          colId: col.colId!,
          sort: col.sort as "asc" | "desc",
        }));
    }, []);

    /**
     * Export full data via backend API
     */
    const exportFull = useCallback(
      async (format: ExportFormat): Promise<void> => {
        const api = gridRef.current?.api;

        if (!api) {
          console.warn("exportFull: Grid API not ready");
          return;
        }

        if (!gridKey) {
          console.warn("exportFull: gridKey prop is required for full export");
          // Fallback to current page export
          exportCurrentPage(format);
          return;
        }

        setIsExporting(true);
        onExportStart?.(format);

        try {
          // Gather current grid state
          const filterModel = api.getFilterModel() ?? {};
          const sortModel = getCurrentSortModel();
          const columns = getCurrentColumnState();

          // Build legacy filter for backend
          const legacyFilter = buildLegacyFilterFromModel(
            filterModel,
            filterKeyMap,
          );

          const payload: GridExportPayload = {
            gridKey,
            format,
            filterModel,
            sortModel,
            searchText,
            sourceCode,
            filter: legacyFilter,
            columns,
            gridParams, // Pass through grid-specific params
          };

          const blob = await exportGridData(payload);

          if (blob) {
            const filename = generateExportFilename(gridKey, format);
            downloadBlob(blob, filename);
            onExportComplete?.(format, true);
          } else {
            throw new Error("Export returned empty response");
          }
        } catch (error) {
          console.error("Export failed:", error);
          onExportError?.(format, error as Error);
          onExportComplete?.(format, false);
        } finally {
          setIsExporting(false);
        }
      },

      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        gridKey,
        gridParams,
        searchText,
        sourceCode,
        filterKeyMap,
        getCurrentSortModel,
        getCurrentColumnState,
        onExportStart,
        onExportComplete,
        onExportError,
      ],
    );

    /**
     * Export current page (client-side, uses AG Grid built-in)
     */
    const exportCurrentPage = useCallback(
      (format: ExportFormat): void => {
        const api = gridRef.current?.api;
        if (!api) return;

        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[:-]/g, "");
        const filename = `${gridKey || "export"}_page_${timestamp}`;

        if (format === "excel") {
          api.exportDataAsExcel({
            fileName: `${filename}.xlsx`,
            sheetName: "Data",
          });
        } else {
          api.exportDataAsCsv({
            fileName: `${filename}.csv`,
          });
        }
      },
      [gridKey],
    );

    // ============================================
    // CONTEXT MENU
    // ============================================

    const getContextMenuItems = useCallback(
      (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
        const result: (DefaultMenuItem | MenuItemDef)[] = [
          "copy",
          "copyWithHeaders",
          "separator",
        ];

        // Current page export (always available) - 
        // disabling this for now since full export is the main use case and
        // we want to avoid confusion with two export options in the menu. 
        // Can re-enable if there's demand for quick current page export.
        // result.push({
        //   name: "Export Current Page",
        //   icon: '<span class="ag-icon ag-icon-save"></span>',
        //   subMenu: [
        //     {
        //       name: "Excel (.xlsx)",
        //       action: () => exportCurrentPage("excel"),
        //       icon: '<span class="ag-icon ag-icon-excel"></span>',
        //     },
        //     {
        //       name: "CSV (.csv)",
        //       action: () => exportCurrentPage("csv"),
        //       icon: '<span class="ag-icon ag-icon-csv"></span>',
        //     },
        //   ],
        // });

        // Full export (only if gridKey provided and enabled)
        if (gridKey && enableFullExport) {
          result.push({
            name: isExporting ? "Exporting..." : "Export Data",
            icon: '<span class="ag-icon ag-icon-save"></span>',
            disabled: isExporting,
            subMenu: isExporting
              ? undefined
              : [
                  {
                    name: "Excel (.xlsx)",
                    action: () => exportFull("excel"),
                    icon: '<span class="ag-icon ag-icon-excel"></span>',
                  },
                  {
                    name: "CSV (.csv)",
                    action: () => exportFull("csv"),
                    icon: '<span class="ag-icon ag-icon-csv"></span>',
                  },
                ],
          });
        }

        result.push("separator", "autoSizeAll", "resetColumns");

        return result;
      },
      [gridKey, enableFullExport, isExporting, exportCurrentPage, exportFull],
    );

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      refreshGrid: () => {
        const api = gridRef.current?.api as any;
        if (!api) return;

        // 1) nuke the initial cache so we don’t short-circuit
        initialCacheRef.current = null;

        // 2) mark as not loaded
        hasLoadedOnce.current = false;

        // 3) clear selection (optional)
        api.deselectAll?.();

        if (rowModelType === "serverSide") {
          // 4) purge SSRM store and reattach datasource
          api.refreshServerSide?.({ purge: true }) ||
            api.refreshServerSideStore?.({ purge: true });

          api.setGridOption?.("serverSideDatasource", getDataSource());
          // if you prefer the legacy API name:
          api.setServerSideDatasource?.(getDataSource());
        } else {
          // clientSide: just repaint; parent should update rowData/cachedData
          api.refreshCells?.({ force: true });
        }
      },
      getSelectedRows: () => gridRef.current?.api.getSelectedRows() || [],
      deselectAllRows: () => gridRef.current?.api.deselectAll(),
      exportFull,
      exportCurrentPage,
      api: gridRef.current?.api,
    }));

    // put this once in your app bootstrap
    useEffect(() => {
      const media = window.matchMedia("(prefers-color-scheme: dark)");
      const apply = () =>
        document.body.setAttribute(
          "data-ag-theme-mode",
          media.matches ? "light" : "light",
        );
      apply();
      media.addEventListener("change", apply);
      return () => media.removeEventListener("change", apply);
    }, []);
    // You can also trigger export programmatically:
    // const handleExportButtonClick = () => {
    //   gridRef.current?.exportFull("excel");
    // };

    return (
      <div
        className={`${styles.cygnusGridHost} ${styles.cygnusGridContainer} ${
          floatingFilters && styles.withFloatingFilters
        } ${isExporting && styles.exporting}`}
      >
        <AgGridReact
          // theme={cygnusTheme}
          ref={gridRef}
          context={gridContext}
          columnDefs={columnDefs}
          defaultColDef={effectiveDefaultColDef}
          // Enterprise Server-Side Row Model
          rowModelType={rowModelType}
          // Pagination (server-side)
          pagination={rowModelType === "serverSide"}
          paginationPageSize={pageSizeState}
          // Request block size for SSRM
          cacheBlockSize={pageSizeState}
          // Client-side mode row data
          rowData={rowModelType === "clientSide" ? cachedData : undefined}
          // Optional row reorder support
          rowDragManaged={Boolean(
            enableRowReorder && rowModelType === "clientSide",
          )}
          suppressMoveWhenRowDragging={Boolean(
            enableRowReorder && rowModelType === "clientSide",
          )}
          onRowDragEnd={(event) => onRowReorder?.(event)}
          onGridReady={onGridReady}
          floatingFiltersHeight={20}
          onFirstDataRendered={(params) => {
            // autoSizeAll(params.api);
            onFirstDataRendered?.(params);
          }}
          onRowSelected={(event) => onRowSelected?.(event)}
          // autoSizeStrategy={{ type: "fitCellContents" }}
          loading={internalLoading || !!cachedIsLoading}
          onSelectionChanged={() => {
            setTimeout(() => {
              const selectedRows = gridRef.current?.api.getSelectedRows() || [];
              onSelectionChange?.(selectedRows);
            }, 0);
          }}
          loadingOverlayComponent={CustomLoadingOverlay}
          noRowsOverlayComponent={CustomNoRowsOverlay}
          rowSelection={rowSelection}
          domLayout="normal"
          rowHeight={25}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={singleClickEdit}
          suppressRowHoverHighlight={suppressRowHoverHighlight}
          suppressColumnVirtualisation={true}
          alwaysShowHorizontalScroll={true}
          sideBar={{
            toolPanels: ["columns", "filters"],
            // defaultToolPanel: "filters",
          }}
          paginationPageSizeSelector={[20, 30, 50, 100]}
          onPaginationChanged={(params) => {
            const newPageSize = params.api.paginationGetPageSize();
            setPageSizeState((prevPageSize) => {
              if (newPageSize > prevPageSize) {
                const api = gridRef.current?.api;
                if (api) {
                  hasLoadedOnce.current = false;
                  initialCacheRef.current = null;
                  api.refreshServerSide?.({ purge: true });
                  api.setGridOption?.("serverSideDatasource", getDataSource());
                }
              }
              return newPageSize;
            });
          }}
          getContextMenuItems={getContextMenuItems}
        />
      </div>
    );
  },
);

export default CygnusGrid;
